import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';

interface ShoppingTileProps {
  imageLink: string | null;
  beforeTitle: string;
  /** Der Titel, der wirklich hochgeladen wuerde: aus editState, nicht aus product. */
  afterTitle: string;
  /** Fertig formatiert MIT Waehrung, oder null. Die Kachel formatiert nie selbst. */
  price: string | null;
  link: string | null;
  /**
   * Steht auf dieser Karte ein Handkorrektur-Stand am TITEL?
   *
   * Gebildet im `WorkbenchReviewer` (Herleitung dort): waehrend des Tippens
   * ein Vergleich genau des Titelfeldes, nach dem Speichern das produktweite
   * `wasEdited`. Die lebende Haelfte ist absichtlich titelgebunden - die Marke
   * sitzt unmittelbar ueber dem Titeltext und wird als Aussage ueber DIESEN
   * Text gelesen.
   *
   * Was sie NICHT leistet: sie ist Sitzungszustand (`editedSet` in
   * `useWorkbench`) und ueberlebt weder einen Reload noch einen Feedwechsel.
   * Nach einem Reload traegt ein von Hand korrigierter Titel die Marke nicht
   * mehr. Eine dauerhafte Herkunftsangabe braeuchte eine persistente Quelle
   * (Spalte oder `optimization_events`-Eintrag) und ist ein eigener Vorgang.
   */
  isManualEdit?: boolean;
}

/**
 * Eine krumme URL ist kein Grund, eine Domain zu erfinden.
 */
function domainOf(link: string | null): string | null {
  if (!link) return null;
  try {
    return new URL(link).hostname;
  } catch {
    return null;
  }
}

/**
 * Die Shopping-Kachel als neutraler Nachbau, ausdruecklich KEINE Nachbildung
 * der Google-Oberflaeche.
 *
 * Eine Kachel statt zwei: eine Optimierung aendert `image_link` nie, und den
 * Preis auch nicht. Der einzige Unterschied ist der Titel, und der steht hier
 * uebereinander (alt durchgestrichen, neu darunter) statt in zwei Kacheln
 * nebeneinander, die zu neun Zehnteln dasselbe zeigen.
 *
 * Sie traegt das EINZIGE Bild der Karte.
 */
export function ShoppingTile({
  imageLink,
  beforeTitle,
  afterTitle,
  price,
  link,
  isManualEdit = false,
}: ShoppingTileProps) {
  const { t } = useTranslation('workbench');
  const { kachelVorherTitel, kachelBild } = useEntwurf();
  /* 128 px heute, 192 px gross. Die Kachel ist der emotionale Anker der
     Karte; die Differenz von 64 px geht eins zu eins der linken Spalte ab
     und wird am laufenden Bild gemessen, nicht gerechnet. */
  const bildHoehe = kachelBild === 'gross' ? 'h-48' : 'h-32';
  /* Ein Bild, das nicht laedt, ist fuer den Pruefer dasselbe wie keines: er
     sieht die Kachel ohne Bild. Bis 09.09.2026 wurde das `<img>` bei einem
     Ladefehler still ausgeblendet (display: none), und die Kachel sah aus,
     als haette die Karte nie ein Bild gehabt. Anlass: beutel-shop.ch sitzt
     hinter Cloudflare, das Bild-Hotlinks mit 403 beantwortet, und der
     Pruefer hielt das fuer einen Fehler der Werkbank. Jetzt zeigt die Kachel
     denselben Platzhalter wie bei fehlendem Bild, nur mit dem anderen Satz:
     "nicht abrufbar" statt "nicht hinterlegt". Der Zustand wird beim Wechsel
     des Bildes zurueckgesetzt, sonst bliebe der Platzhalter am naechsten
     Produkt kleben. */
  const [imageBroken, setImageBroken] = useState(false);
  useEffect(() => {
    setImageBroken(false);
  }, [imageLink]);
  const domain = domainOf(link);
  const showBefore =
    kachelVorherTitel === 'zeigen' &&
    beforeTitle.trim() !== '' &&
    beforeTitle.trim() !== afterTitle.trim();

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      {imageLink && !imageBroken ? (
        /* Der Bildteller (09.09.2026). Das Foto sass vorher nackt auf
           `bg-card`, und `bg-card` kippt mit dem Theme — Produktfotografie
           nicht: sie ist auf Weiss aufgenommen oder liegt als PNG mit
           transparentem Hintergrund vor. Im Dunkelmodus hiess das, dass ein
           weisses Foto mit 13.22:1 auf der Karte stand (hell: 1.00:1), und
           zwar mit einer Kante dort, wo die weissen Pixel zufaellig aufhoeren
           statt am Rand der Kachel; ein transparentes PNG liess die dunkle
           Karte durchscheinen und machte ein dunkles Produkt unbeurteilbar.
`--product-plate` ist das einzige Token dieser App, das
           in beiden Themes DENSELBEN Wert traegt, und kippt deshalb nicht: das
           weisse Foto verschwindet darin (1.09:1), im hellen Modus ist der
           Teller gegen die weisse Karte ebenso unsichtbar (1.09:1), die
           Flaeche aendert sich dort also nicht. Die Gleichheit beider Themes
           ist gepinnt (src/test/contrast.test.ts). Den Sprung nimmt das
           nicht weg — das kann nichts —, aber er sitzt jetzt an einer festen,
           gerundeten Kante. Der Ring im Dunkelmodus traegt die Randfarbe der
           App: innen deutlich (8.35:1 gegen den Teller), aussen fast die
           Karte (1.45:1), der Teller liest sich damit als gerahmte Flaeche
           statt als Loch. Messwerte und Waechter:
           `src/components/workbench/produktbildTeller.test.tsx`.

           `rounded`, nicht `rounded-md`: in DIESER Konfiguration ist
           `rounded-md` auf `var(--radius)` gesetzt (tailwind.config.ts) und
           damit 12 px, nicht die 6 px des Tailwind-Standards. 12 px waeren
           genauso rund wie die Kachel selbst, die das Bild mit 12 px
           Polsterung umgibt — der ideale Innenradius ist dort 0. Und der
           Platzhalter acht Zeilen tiefer traegt `rounded`: ein Produkt, dessen
           Bild nicht laedt, aenderte sonst die Eckform derselben Box. */
        <img
          src={imageLink}
          alt=""
          className={`mb-2 ${bildHoehe} w-full rounded bg-product-plate p-1 object-contain dark:ring-1 dark:ring-inset dark:ring-border`}
          loading="lazy"
          onError={() => setImageBroken(true)}
        />
      ) : (
        <div
          data-testid="tile-no-image"
          data-reason={imageLink ? 'blocked' : 'missing'}
          className={`mb-2 flex ${bildHoehe} w-full flex-col items-center justify-center gap-1 rounded bg-muted`}
        >
          <ImageOff className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <span className="px-2 text-center text-[10px] text-muted-foreground">
            {imageLink ? t('tile.imageBlocked') : t('tile.noImage')}
          </span>
        </div>
      )}

      {showBefore && (
        <p
          data-testid="tile-before-title"
          className="line-clamp-1 text-[11px] leading-tight text-muted-foreground line-through"
        >
          <span className="sr-only">{t('tile.beforeLabel')}: </span>
          {beforeTitle}
        </p>
      )}

      {isManualEdit && (
        /* Die Marke sitzt an dem Text, den sie meint - nicht im Kartenkopf
           (Spec 8 und 13). Tonales Paar statt `variant="warning"`: weiss auf
           deckendem Orange misst 2.13:1 hell und 1.69:1 dunkel. */
        <span
          data-testid="tile-your-version"
          className="mb-1 inline-block rounded bg-warning-light px-1.5 py-0.5 text-[10px] font-medium leading-none text-warning-ink"
        >
          {t('tile.yourVersion')}
        </span>
      )}

      {/* Zwei Zeilen wie bei Google, danach abgeschnitten. Die Erklaerung
          dazu steht als Bildunterschrift darunter (CustomerColumn). */}
      <p data-testid="tile-after-title" className="line-clamp-2 text-xs font-medium leading-tight">
        {afterTitle}
      </p>

      {price ? (
        <p className="mt-1.5 text-sm font-semibold tabular-nums">{price}</p>
      ) : (
        <p
          data-testid="tile-no-price"
          className="mt-1.5 text-[10px] leading-tight text-muted-foreground"
        >
          {t('tile.noPrice')}
        </p>
      )}

      {domain && (
        <p data-testid="tile-domain" className="mt-0.5 truncate text-[10px] text-muted-foreground">
          {domain}
        </p>
      )}
    </div>
  );
}
